import {
  ApiResponse,
  accountBaseUrl,
  getData,
  postData,
  patchData,
  deleteData,
} from "../../constServices";

/** Suscripción recurrente del plan con Culqi (spec 006/US6). */

export interface ISubscriptionAddon {
  id: number;
  /**
   * Capacidad (user/comanda_user/cashbox/warehouse/sede) o add-on funcional
   * por sede (fidelizacion, ai_analytics, facturacion_electronica, whatsapp_*).
   */
  type:
    | "user"
    | "comanda_user"
    | "cashbox"
    | "warehouse"
    | "sede"
    | "fidelizacion"
    | "ai_analytics"
    | "facturacion_electronica"
    | "whatsapp_recordatorios"
    | "whatsapp_marketing"
    | "whatsapp_bundle";
  quantity: number;
  unitPrice: number;
  recurringPerCycle: number;
  /** Spec 031/D6: fecha de baja programada (fin de ciclo) o null. */
  scheduledRemovalAt: string | null;
  /**
   * Spec 044 (H1/H3, aditivo): sede para la que se contrató IA/Fidelización
   * en una suscripción de empresa. Ausente = vale para todas las sedes.
   */
  localId?: number | null;
  localName?: string | null;
}

/** Spec 031: cambios diferidos al próximo cambio de ciclo. */
export type IScheduledChange =
  | {
      type: "addon_removal";
      addonId: number;
      addonType: string;
      effectiveAt: string;
    }
  | {
      type: "downgrade";
      downgradeId: number;
      targetPlanId: number;
      targetPlanName: string | null;
      effectiveAt: string;
      status: string;
    };

export interface IRecurrentSubscription {
  active: boolean;
  status: "activa" | "gracia" | "suspendida" | "cancelada" | null;
  planId: number | null;
  planKey: string | null;
  planName: string | null;
  period: "mensual" | "trimestral" | "semestral" | "anual" | null;
  recurringAmount: number;
  currency: string;
  nextChargeAt: string | null;
  cycleEndsAt: string | null;
  retries: number;
  addons: ISubscriptionAddon[];
  card: { brand: string; lastFour: string } | null;
  mode: "centralizado" | "individualizado";
  /** Spec 031/D1: cuenta en prueba de 14 días del Plan Estándar. */
  isTrial: boolean;
  trialDaysLeft: number | null;
  /**
   * Spec 044 (F-R1-4, aditivo pendiente en backend): fin exacto de la prueba
   * (ISO). Con él la UI distingue "termina hoy" de "termina mañana".
   */
  trialEndsAt?: string | null;
  /** T3-B (F-R1-4): la prueba ya terminó (su último día quedó atrás en Lima). */
  trialExpired?: boolean;
  /**
   * T3-B (F-N2-3): con el cierre de mes activo, fechas del responsable
   * (`paidThrough` sin gracia, `nextChargeAt` solo automático, `payBefore`
   * en mes a mes). null/ausente = sin cierre.
   */
  cutoffBilling?: {
    mode: "automatic" | "monthly";
    paidThrough: string | null;
    nextChargeAt: string | null;
    payBefore: string | null;
    graceUntil: string | null;
  } | null;
  scheduledChanges: IScheduledChange[];
  /** Spec 031/D9: desglose base imponible + IGV del recurrente. */
  taxBreakdown: { base: number; igv: number };
}

export const getRecurrentSubscription_service =
  async (): Promise<ApiResponse> =>
    getData(
      `${accountBaseUrl}/subscription`,
      "Suscripción obtenida",
      "No se pudo obtener la suscripción"
    );

export const getSubscriptionPlanPrices_service =
  async (): Promise<ApiResponse> =>
    getData(
      `${accountBaseUrl}/subscription/plan-prices`,
      "Precios obtenidos",
      "No se pudieron obtener los precios de planes"
    );

export const getSubscriptionAddonPrices_service =
  async (): Promise<ApiResponse> =>
    getData(
      `${accountBaseUrl}/subscription/addon-prices`,
      "Precios obtenidos",
      "No se pudieron obtener los precios de adicionales"
    );

export const affiliateSubscription_service = async (payload: {
  planKey: string;
  period: string;
  methodId?: number;
}): Promise<ApiResponse> =>
  postData(
    `${accountBaseUrl}/subscription`,
    payload,
    "Suscripción activada",
    "No se pudo activar la suscripción"
  );

export const changeSubscription_service = async (payload: {
  planKey?: string;
  period?: string;
  methodId?: number;
  useCredit?: boolean;
}): Promise<ApiResponse> =>
  patchData(
    `${accountBaseUrl}/subscription`,
    payload,
    "Suscripción actualizada",
    "No se pudo actualizar la suscripción"
  );

/**
 * Cotización (solo lectura) de un cambio de plan y/o ciclo. No cobra nada:
 * alimenta el modal de confirmación de "Mi suscripción" con los montos
 * exactos del servidor antes de ejecutar `changeSubscription_service`.
 */
export interface ISubscriptionChangePreview {
  planKey: string;
  planName: string;
  period: string;
  months: number;
  isUpgrade: boolean;
  isDowngrade: boolean;
  isToAnnual: boolean;
  /** Bruto (IGV incluido). */
  todayCharge: number;
  /** false cuando el cargo es menor al mínimo cobrable y se consolida luego. */
  todayChargeApplies: boolean;
  todayChargeNet: number;
  todayChargeTax: number;
  chargeConcept: string | null;
  /** Bruto (IGV incluido). */
  newRecurring: number;
  newRecurringNet: number;
  newRecurringTax: number;
  effectiveFrom: string | null;
  absorbedAddons: { type: string; quantity: number }[];
  /**
   * Spec 044 (F-R2-11, aditivo pendiente en backend): la vista previa detecta
   * que el cambio no se puede programar (p. ej. FRANCHISES_ACTIVE).
   */
  blocked?: { code: string; message?: string | null } | null;
  currentRecurring: number;
  currentPlanKey: string | null;
  currentPeriod: string | null;
}

export const previewSubscriptionChange_service = async (payload: {
  planKey?: string;
  period?: string;
}): Promise<ApiResponse> =>
  postData(
    `${accountBaseUrl}/subscription/change/preview`,
    payload,
    "Cambio cotizado",
    "No se pudo calcular el cambio de tu suscripción"
  );

export const cancelRecurrentSubscription_service =
  async (): Promise<ApiResponse> =>
    deleteData(
      `${accountBaseUrl}/subscription`,
      "Suscripción cancelada",
      "No se pudo cancelar la suscripción"
    );

export const addSubscriptionAddon_service = async (payload: {
  type: string;
  quantity: number;
  methodId?: number;
  useCredit?: boolean;
}): Promise<ApiResponse> =>
  postData(
    `${accountBaseUrl}/subscription/addons`,
    payload,
    "Adicional agregado",
    "No se pudo agregar el adicional"
  );

export const removeSubscriptionAddon_service = async (
  id: number
): Promise<ApiResponse> =>
  deleteData(
    `${accountBaseUrl}/subscription/addons/${id}`,
    "Baja programada para la próxima renovación",
    "No se pudo programar la baja del adicional"
  );

/** Spec 031/FR-023: cancela una baja programada. */
export const cancelAddonRemoval_service = async (
  id: number
): Promise<ApiResponse> =>
  postData(
    `${accountBaseUrl}/subscription/addons/${id}/cancel-removal`,
    {},
    "Baja cancelada",
    "No se pudo cancelar la baja"
  );

/** Spec 031/FR-025: downgrade programado al cierre de ciclo. */
export const scheduleDowngrade_service = async (payload: {
  targetPlan: string;
  keep?: {
    users?: number[];
    comandaUsers?: number[];
    cashboxes?: number[];
    warehouses?: number[];
  };
}): Promise<ApiResponse> =>
  postData(
    `${accountBaseUrl}/subscription/downgrade`,
    payload,
    "Downgrade programado",
    "No se pudo programar el downgrade"
  );

export const getDowngrade_service = async (): Promise<ApiResponse> =>
  getData(
    `${accountBaseUrl}/subscription/downgrade`,
    "Downgrade obtenido",
    "No se pudo obtener el downgrade"
  );

export const cancelDowngrade_service = async (
  id: number
): Promise<ApiResponse> =>
  deleteData(
    `${accountBaseUrl}/subscription/downgrade/${id}`,
    "Downgrade cancelado",
    "No se pudo cancelar el downgrade"
  );
